import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  memo,
  useCallback,
  useImperativeHandle,
} from "react";
import { useI18n } from "Hooks";
import _ from "lodash";
import { connect } from "react-redux";
import classNames from "classnames";

// components
import { FormTextAreaInput as TextAreaInput } from "UIComponents/TextAreaInput";
import AddLine from "UIComponents/AddLine";
import FullScreenLoader from "UIComponents/FullScreenLoader";

// modules
import {
  updateStandardRubricCriterias,
  updateStandardRubricDescriptors,
  removeStandardRubricDescriptors,
  bulkCreateStandardRubricDescriptors,
} from "AppComponents/StandardRubric/modules/StandardRubricModule";
import { writeStandardRubricElementInCache } from "AppComponents/StandardRubric/modules/StandardRubricGraphqlHelpers";
import { getAssessmentToolNodeDetails } from "modules/AssessmentTool/AssessmentToolModule";

// design system
import {
  Tooltip,
  IconButton,
  DropdownMenu,
  Dropdown,
  ButtonDisclosure,
} from "@toddle-edu/ds-web";
import {
  WarningTriangleFilled,
  ReloadArrowOutlined,
  DotsVerticalOutlined,
} from "@toddle-edu/ds-icons";

// styles and constants
import { fontStyles } from "@toddle-edu/ds-theme";
import classes from "./StandardRubricEditCell.scss";
import {
  CODE,
  CRITERIA_DESCRIPTION,
  GRADE_SCALE_VALUE,
  RATING_DESCRIPTION,
} from "../../../../modules/Constants";

// utils
import {
  getColumnId,
  getRowId,
} from "AppComponents/StandardRubric/modules/Utils";
import { getKeyboardFocusableElements } from "Utils/AccessibilityHelpers";
import { getElementType } from "../../../../modules/Utils";

// shared text area styles
const commonTextAreaStyles = Object.freeze({
  borderRadius: 0,
  border: "none",
  padding: "8px",
  fontSize: "1.4rem",
  lineHeight: "2rem",
  color: "var(--text-primary)",
  overflow: "hidden",
  scrollbarWidth: "none" /* Firefox */,
  msOverflowStyle: "none" /* IE and Edge */,
  resize: "none",
  ...fontStyles.medium,
});

// component-specific styles
const styles = {
  titleTextAreaStyles: {
    ...commonTextAreaStyles,
    padding: "8px 8px 4px",
  },
  descriptionTextAreaStyles: {
    ...commonTextAreaStyles,
    fontSize: "1.2rem",
    lineHeight: "1.6rem",
    color: "var(--text-secondary)",
    padding: "4px 8px 8px",
  },
  pointTextAreaStyles: {
    ...commonTextAreaStyles,
    padding: "6px 8px",
  },
  errorTextAreaStyles: {
    color: "var(--text-semantic-error)",
  },
  surfaceEnabled: {
    backgroundColor: "var(--surface-primary-enabled)",
  },
  dropdownButton: {
    padding: "4px",
  },
  descriptionWithDropdownLabel: {
    padding: "0px 14px 8px",
  },
  scoreWithDropdownLabel: {
    padding: "8px 14px",
  },
  criteriaLabel: {
    padding: "8px 12px",
  },
};

const renderDropdownLabel = (option, showLabel = true) => {
  const { abbreviation, label } = option || {};

  return option ? (
    <span>
      <span>{abbreviation}</span>
      {showLabel && label ? (
        <span className={classes.subtleItalic}>{` (${label})`}</span>
      ) : null}
    </span>
  ) : null;
};

const StandardRubricEditCell = memo(props => {
  const {
    testId,
    item,
    toolId,
    isDragging,
    isCriteria = false,
    descriptionConfig,
    duplicateDescriptorScores = [],
    duplicateGradeValues = [],
    pointTextAreaRefs,
    labelTextAreaRefs,
    descriptionTextAreaRefs,
    getAssessmentToolNodeDetails,
    updateStandardRubricCriterias,
    updateStandardRubricDescriptors,
    removeStandardRubricDescriptors,
    bulkCreateStandardRubricDescriptors,
    onAddDescriptor,
    saveStandardRubricUpdateActivityStatus,
    sortDirection,
    localRubricCriteria = [],
    isStandardBasedRubric,
    showScore,
    type: assessmentToolType,
    allowGradeScaleValueSelection = false,
    isNumericGradeScale = false,
    gradeScaleValues,
    onChangeIsDropdownVisible,
    standardGradeScaleValueDescriptorsMapping,
    customRef: ref,
    isLastDescriptor = false,
  } = props;

  const {
    id,
    label,
    description,
    maxScore,
    score,
    standard,
    gradeScaleValue,
    standardGradeScaleValueDescriptor,
  } = item;

  const {
    id: gradeScaleValueId,
    label: gradeScaleValueLabel,
    abbreviation: gradeScaleAbbreviation,
  } = gradeScaleValue || {};

  const descriptionConfigValue = _.map(descriptionConfig, "value");

  // Helper functions to calculate derived values
  const _getUpdatedLabel = useCallback(() => {
    if (!isStandardBasedRubric) return label;
    if (standard?.label) return standard.label;
    if (_.isNil(isNumericGradeScale)) return "-";

    return gradeScaleValueLabel || "-";
  }, [
    isStandardBasedRubric,
    label,
    standard,
    isNumericGradeScale,
    gradeScaleValueLabel,
  ]);

  const _getUpdatedDescription = useCallback(() => {
    if (isStandardBasedRubric) {
      return description ?? standardGradeScaleValueDescriptor?.description;
    }

    return description ?? "";
  }, [description, standardGradeScaleValueDescriptor, isStandardBasedRubric]);

  // states
  const [localLabel, setLocalLabel] = useState(() => _getUpdatedLabel());
  const [localDescription, setLocalDescription] = useState(() =>
    _getUpdatedDescription()
  );
  const [localScore, setLocalScore] = useState(score);

  const [isReordering, setIsReordering] = useState(false);
  const [shouldReorder, setShouldReorder] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);
  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);

  const [shouldUpdateLocalState, setShouldUpdateLocalState] = useState(
    isStandardBasedRubric
  );

  // Update local state whenever source props change, but only if values are different
  useEffect(() => {
    const newLabel = _getUpdatedLabel();

    if (newLabel !== localLabel && shouldUpdateLocalState) {
      setLocalLabel(newLabel);
    }
  }, [
    label,
    standard,
    gradeScaleValueLabel,
    isStandardBasedRubric,
    standard,
    isNumericGradeScale,
    shouldUpdateLocalState,
  ]);

  useEffect(() => {
    const newDescription = _getUpdatedDescription();

    if (newDescription !== localDescription && shouldUpdateLocalState) {
      setLocalDescription(newDescription);
    }
  }, [
    description,
    standardGradeScaleValueDescriptor,
    standard,
    shouldUpdateLocalState,
  ]);

  useEffect(() => {
    if (score !== localScore && shouldUpdateLocalState) {
      setLocalScore(score);
    }
  }, [score, shouldUpdateLocalState]);

  // hooks
  const { t } = useI18n();

  // refs
  const debouncedFunctionsRef = useRef({});
  const reorderingTimerRef = useRef(null);

  // Add effect to handle reordering when shouldReorder becomes true
  useEffect(() => {
    // Only proceed if both conditions are met: shouldReorder is true and the input has been blurred
    if (shouldReorder && hasBlurred) {
      // Execute reordering logic
      handleReordering();

      // Reset the flags
      setHasBlurred(false);
    }
  }, [shouldReorder, hasBlurred]);

  // Initialize the debounced functions manager
  useEffect(() => {
    const debouncedFunctions = debouncedFunctionsRef.current;
    return () => {
      _.values(debouncedFunctions).forEach(fn => fn?.cancel?.());

      // Clear any remaining timers
      if (reorderingTimerRef.current) {
        clearTimeout(reorderingTimerRef.current);
      }
    };
  }, []);

  // Expose flush function through ref
  useImperativeHandle(
    ref,
    () => ({
      flushDebouncedFunctions: () => {
        const debouncedFunctions = debouncedFunctionsRef.current;
        _.values(debouncedFunctions).forEach(fn => fn?.flush?.());
      },
    }),
    []
  );

  // Get a debounced function specific to a field type (label, description, score)
  const getDebouncedFunction = (fieldType, time = 1000) => {
    const key = `${id}_${fieldType}`;

    if (!debouncedFunctionsRef.current[key]) {
      const updateFn = isCriteria
        ? updateStandardRubricCriterias
        : updateStandardRubricDescriptors;

      debouncedFunctionsRef.current[key] = _.debounce((input, callback) => {
        updateFn({
          input,
          toolId,
          type: assessmentToolType,
        })
          .then(() => {
            if (typeof callback === "function") callback();
          })
          .catch(() => {});
      }, time);
    }

    return debouncedFunctionsRef.current[key];
  };

  // variables

  const type = isCriteria
    ? "STANDARD_RUBRIC_CRITERIA"
    : "STANDARD_RUBRIC_DESCRIPTOR";

  const pointPlaceholder = t(`common:${isCriteria ? "total_points" : "point"}`);
  const pointValue = isCriteria && maxScore ? `${maxScore} pts` : localScore;

  // check if description should be shown based on configuration
  const showDescription = isCriteria
    ? !isStandardBasedRubric &&
      _.includes(descriptionConfigValue, CRITERIA_DESCRIPTION)
    : _.includes(descriptionConfigValue, RATING_DESCRIPTION);

  const showStandardCode =
    isCriteria && _.includes(descriptionConfigValue, CODE);

  const showGradeScaleValue =
    !isCriteria && _.includes(descriptionConfigValue, GRADE_SCALE_VALUE);

  const parentCriterion = useMemo(
    () =>
      _.find(localRubricCriteria, criterion =>
        _.some(criterion.descriptors, { id })
      ),
    [localRubricCriteria, id]
  );

  // check if point value is duplicate
  const showUniqueGradeValuesError =
    allowGradeScaleValueSelection &&
    _.includes(duplicateGradeValues, gradeScaleValueId);

  const showUniquePointError = _.includes(duplicateDescriptorScores, score);

  const disabled = _.includes(id, "NEW_");

  const showResetToDefaultIcon =
    isStandardBasedRubric &&
    !isCriteria &&
    !_.isUndefined(standardGradeScaleValueDescriptor?.description) &&
    localDescription !== standardGradeScaleValueDescriptor?.description;

  const copyToOtherCriterionsOptions = useMemo(() => {
    return [
      {
        label: t("classRoom:copy_this_grade_scale_for_all_criterions"),
        key: "copy_this_grade_scale_for_all_criterions",
      },
    ];
  }, [t]);

  const gradeScaleValueOptions = _.map(gradeScaleValues, option => ({
    ...option,
    label: renderDropdownLabel(option, showGradeScaleValue),
  }));

  const gradeScaleSelectedValue = useMemo(() => {
    return (
      _.find(gradeScaleValues, option => option?.key === gradeScaleValueId)
        ?.key || null
    );
  }, [gradeScaleValueId, gradeScaleValues]);

  // Determine if copy to other criteria button should be shown
  const showCopyToOtherCriteriaButton = useMemo(() => {
    // Don't show the button if there are no criteria or just one criterion
    if (_.size(localRubricCriteria) <= 1) {
      return false;
    }

    const currentCriterion = _.find(
      localRubricCriteria,
      criterion => criterion?.id === id
    );

    if (!currentCriterion) {
      return false;
    }

    const currentCriterionDescriptors = _.get(
      currentCriterion,
      "descriptors",
      []
    );

    // Check if any other criterion is different from the current one
    const hasDifferentCriterion = _.some(localRubricCriteria, criterion => {
      if (criterion.id === currentCriterion.id) return false;

      const otherDescriptors = _.get(criterion, "descriptors", []);

      // If descriptor counts are different, they're not the same
      if (_.size(otherDescriptors) !== _.size(currentCriterionDescriptors)) {
        return true;
      }

      // Check if any descriptor is different (comparing grade scale values or descriptions)
      return _.some(otherDescriptors, (descriptor, index) => {
        const currentDescriptor = currentCriterionDescriptors[index];
        return (
          descriptor?.gradeScaleValue?.id !==
          currentDescriptor?.gradeScaleValue?.id
        );
      });
    });

    // Show the button only if there are differences to copy
    return hasDifferentCriterion;
  }, [localRubricCriteria, id]);

  // compute point text area styles based on error state
  const pointTextAreaStyles = useMemo(
    () => ({
      ...styles.pointTextAreaStyles,
      ...(showUniquePointError && styles.errorTextAreaStyles),
      ...(((isCriteria && !disabled) || isStandardBasedRubric) &&
        styles.surfaceEnabled),
      ...(allowGradeScaleValueSelection && styles.scoreWithDropdownLabel),
    }),
    [
      showUniquePointError,
      isCriteria,
      disabled,
      isStandardBasedRubric,
      allowGradeScaleValueSelection,
    ]
  );

  const labelTextAreaStyles = useMemo(() => {
    return {
      ...styles.titleTextAreaStyles,
      ...(isStandardBasedRubric && styles.surfaceEnabled),
      ...(allowGradeScaleValueSelection && isCriteria && styles.criteriaLabel),
    };
  }, [isStandardBasedRubric, allowGradeScaleValueSelection, isCriteria]);

  const descriptionTextAreaStyles = useMemo(() => {
    return {
      ...styles.descriptionTextAreaStyles,
      ...(allowGradeScaleValueSelection && styles.descriptionWithDropdownLabel),
      ...(isStandardBasedRubric && styles.surfaceEnabled),
    };
  }, [allowGradeScaleValueSelection, isStandardBasedRubric]);

  // functions
  const updatePointInputRef = ref => {
    if (pointTextAreaRefs?.current) {
      pointTextAreaRefs.current[id] = ref;
    }
  };

  const updateLabelInputRef = ref => {
    if (labelTextAreaRefs?.current) {
      labelTextAreaRefs.current[id] = ref;
    }
  };

  const updateDescriptionInputRef = ref => {
    if (descriptionTextAreaRefs?.current) {
      descriptionTextAreaRefs.current[id] = ref;
    }
  };

  const updateLabelField = value => {
    // Update local state for instant update (optimistic update)
    setLocalLabel(value);

    // Call mutation with a debounced function using the new value directly
    getDebouncedFunction("label")({
      id: item.id,
      label: value,
      elementType: !isCriteria ? getElementType(parentCriterion) : undefined,
    });

    // Update activity log status
    saveStandardRubricUpdateActivityStatus();
  };

  const onLabelTextAreaKeyDown = e => {
    const { key } = e;

    if (key === "Tab" && e.shiftKey) {
      e.preventDefault();

      const popoverContent =
        document.getElementById(getColumnId(item.id)) ??
        document.getElementById(getRowId(item.id));

      if (popoverContent) {
        const focusableElements = getKeyboardFocusableElements(popoverContent);

        if (_.size(focusableElements) > 0) {
          // Focus the last element when moving backward
          focusableElements?.[focusableElements.length - 1]?.focus();
        }
      }
    }
  };

  const onBlurLabelField = () => {
    writeStandardRubricElementInCache({
      id: item.id,
      type,
      data: { ...item, label: localLabel },
    });
  };

  const updateDescriptionField = value => {
    // Update local state for instant update (optimistic update)
    setLocalDescription(value);

    // Call mutation with a debounced function specific to description updates for this item
    getDebouncedFunction("description")({
      id: item.id,
      description: value,
      elementType: !isCriteria ? getElementType(parentCriterion) : undefined,
    });

    // Update activity log status
    saveStandardRubricUpdateActivityStatus();
  };

  const onBlurDescriptionField = () => {
    writeStandardRubricElementInCache({
      id: item.id,
      type,
      data: { ...item, description: localDescription },
    });
  };

  // Extract reordering logic into a separate function for reusability
  const handleReordering = () => {
    // Only proceed with reordering if we're not in criteria, have a sort direction, and a valid score
    if (!isCriteria && sortDirection && localScore !== "") {
      if (parentCriterion) {
        const descriptors = _.get(parentCriterion, "descriptors", []);

        // Split descriptors into two groups: with valid scores and without scores
        const validScoreDescriptors = [];
        const nullScoreDescriptors = [];

        _.forEach(descriptors, descriptor => {
          if (
            _.isNil(descriptor.score) ||
            _.isUndefined(descriptor.score) ||
            descriptor.score === ""
          ) {
            nullScoreDescriptors.push(descriptor);
          } else {
            validScoreDescriptors.push(descriptor);
          }
        });

        // Sort only the descriptors with valid scores
        const sortedValidScores = _.sortBy(validScoreDescriptors, descriptor =>
          _.toNumber(descriptor.score)
        );

        // For descending order, reverse the valid scores array
        if (sortDirection === "desc") {
          sortedValidScores.reverse();
        }

        // Combine the sorted valid scores with null scores at the end
        const sortedDescriptors = [
          ...sortedValidScores,
          ...nullScoreDescriptors,
        ];

        // Prepare input for updating display sequence
        const input = _.map(sortedDescriptors, (descriptor, index) => ({
          id: descriptor.id,
          displaySequence: index + 1,
        }));

        // Update the display sequence if needed
        if (_.size(input) > 0) {
          setTimeout(() => {
            updateStandardRubricDescriptors({
              toolId,
              input,
              type: assessmentToolType,
              elementType: !isCriteria
                ? getElementType(parentCriterion)
                : undefined,
            });
          }, 300);
        }

        // Find current position before sorting
        const currentIndex = _.findIndex(descriptors, { id });

        // Find position after sorting
        const newIndex = _.findIndex(sortedDescriptors, { id });

        // Only show reordering indicator if the position actually changes
        const positionChanged = currentIndex !== newIndex;

        if (positionChanged) {
          // Set reordering state to true to show the animation
          setIsReordering(true);

          // Clear any existing timers
          if (reorderingTimerRef.current) {
            clearTimeout(reorderingTimerRef.current);
          }

          // Set a timer to clear the reordering state
          reorderingTimerRef.current = setTimeout(() => {
            setIsReordering(false);
            setShouldReorder(false);
          }, 1000); // Animation will show for 1 second
        } else {
          // If position didn't change, reset flags
          setShouldReorder(false);
        }
      }
    } else {
      // Reset the shouldReorder flag if criteria don't match
      setShouldReorder(false);
    }
  };

  const onBlurPointField = () => {
    // Set the hasBlurred flag to true to indicate the field has been blurred
    setHasBlurred(true);

    // If shouldReorder is already true (mutation completed before blur), execute reordering now
    if (shouldReorder) {
      handleReordering();
      // Reset hasBlurred since we've handled the reordering immediately
      setHasBlurred(false);
    }

    writeStandardRubricElementInCache({
      id: item.id,
      type,
      data: { ...item, score: parseFloat(localScore) },
    });

    // Otherwise, useEffect will handle it when shouldReorder becomes true
  };

  const updatePointField = value => {
    // Allow empty string for clearing the input
    if (value === "") {
      setLocalScore(value);

      writeStandardRubricElementInCache({
        id: item.id,
        type,
        data: { ...item, score: null },
      });

      // Use descriptor update function for points
      getDebouncedFunction("score")(
        {
          id: item.id,
          score: null,
          elementType: !isCriteria
            ? getElementType(parentCriterion)
            : undefined,
        },
        () => {
          // Set shouldReorder flag to true after successful mutation
          setShouldReorder(true);
        }
      );

      return;
    }

    // Validate that the input is a proper positive number (allows decimal points)
    const isValidNumber = /^(\d+|\d+\.\d*)$/.test(value);

    if (isValidNumber && parseFloat(value) >= 0) {
      // 1. update local state for instant update (optimistic update)
      setLocalScore(value);

      // 3. call mutation with a debounced function specific to score updates
      // Pass a callback that sets shouldReorder flag to true after successful completion
      getDebouncedFunction("score")(
        {
          id: item.id,
          score: parseFloat(value),
          elementType: !isCriteria
            ? getElementType(parentCriterion)
            : undefined,
        },
        () => {
          setShouldReorder(true);
        }
      );

      // 4. update activity log status
      saveStandardRubricUpdateActivityStatus();
    }
  };

  const onAddLineKeyDown = e => {
    const { key } = e;

    if (key === "Tab") {
      const isBackward = e.shiftKey;

      // If Shift+Tab, focus on the point text area input of the current cell
      if (isBackward) {
        if (pointTextAreaRefs?.current?.[id]) {
          e.preventDefault();
          pointTextAreaRefs.current[id].focus();
        } else if (descriptionTextAreaRefs?.current?.[id]) {
          e.preventDefault();
          descriptionTextAreaRefs.current[id].focus();
        }

        return;
      }

      // Otherwise, proceed with the original tabbing behavior
      const { items } = props;
      const currentIndex = _.findIndex(items, ({ id }) => id === item.id);
      const targetIndex = currentIndex + (isBackward ? -1 : 1);

      if (targetIndex >= 0 && targetIndex < items.length) {
        e.preventDefault();

        const targetCell = items[targetIndex];
        const popoverContent =
          document.getElementById(getColumnId(targetCell.id)) ||
          document.getElementById(getRowId(targetCell.id));

        if (popoverContent) {
          const focusableElements = getKeyboardFocusableElements(
            popoverContent
          );

          if (_.size(focusableElements) > 0) {
            // Focus first element when going forward, last element when going backward
            const elementToFocus = isBackward
              ? focusableElements?.[focusableElements?.length - 1]
              : focusableElements?.[0];

            elementToFocus?.focus();
          }
        }
      }
    }
  };

  const onClickResetToDefault = useCallback(() => {
    const description = null;

    writeStandardRubricElementInCache({
      id: item.id,
      type,
      data: { ...item, description },
    });

    setLocalDescription(
      standardGradeScaleValueDescriptor?.description || description
    );

    getDebouncedFunction("description")({
      id: item.id,
      description,
      elementType: !isCriteria ? getElementType(parentCriterion) : undefined,
    });
  }, [
    item,
    standardGradeScaleValueDescriptor,
    isCriteria,
    parentCriterion,
    type,
  ]);

  const onSelectGradeScale = async ({ key }) => {
    if (key === gradeScaleValueId) return;

    const { label = "", score = null, abbreviation } =
      _.find(gradeScaleValues, { key }) || {};

    const standardGradeScaleValueDescriptor =
      _.find(standardGradeScaleValueDescriptorsMapping, {
        standardId: parentCriterion?.standard?.id,
        gradeScaleValueId: key,
      }) || {};

    setShouldUpdateLocalState(false);
    setLocalLabel(label);
    setLocalScore(score);
    setLocalDescription(standardGradeScaleValueDescriptor?.description);

    const data = {
      ...item,
      score,
      gradeScaleValue: {
        id: key,
        label,
        abbreviation,
        __typename: "AcademicCriteriaValue",
      },
      standardGradeScaleValueDescriptor: {
        id: _.get(standardGradeScaleValueDescriptor, "id", null),
        description: _.get(
          standardGradeScaleValueDescriptor,
          "description",
          null
        ),
        gradeScaleValue: {
          id: key,
          __typename: "GradeScaleValue",
        },
        __typename: "StandardGradeScaleValueDescriptor",
      },
    };

    writeStandardRubricElementInCache({
      id: item.id,
      type,
      data,
    });

    getDebouncedFunction("label", 0)(
      {
        id,
        gradeScaleValueId: key,
        elementId: _.get(parentCriterion, "standard.id"),
        description: null,
        elementType: !isCriteria ? getElementType(parentCriterion) : undefined,
      },
      () => {
        setShouldUpdateLocalState(true);
      }
    );

    onChangeIsDropdownVisible(false);
  };

  const onCopyGradeScaleToOtherCriterions = async () => {
    const currentCriterion = _.find(
      localRubricCriteria,
      criterion => criterion?.id === id
    );

    if (!currentCriterion) return;

    const currentCriterionDescriptors = _.get(
      currentCriterion,
      "descriptors",
      []
    );

    const updateInput = [];
    const createInput = [];
    const deleteIds = [];

    _.forEach(localRubricCriteria, criterion => {
      if (criterion.id === currentCriterion.id) return;

      const descriptors = _.get(criterion, "descriptors", []);
      const currentCriterionDescriptorsCount = _.size(
        currentCriterionDescriptors
      );
      const otherCriterionDescriptorsCount = _.size(descriptors);

      // Update existing descriptors that should remain
      const descriptorsToKeep = Math.min(
        currentCriterionDescriptorsCount,
        otherCriterionDescriptorsCount
      );

      for (let i = 0; i < descriptorsToKeep; i++) {
        const descriptor = descriptors[i];
        const currentDescriptor = currentCriterionDescriptors[i];

        // Only update if the descriptor is different from current
        const needsUpdate =
          descriptor?.gradeScaleValue?.id !==
          currentDescriptor?.gradeScaleValue?.id;

        if (needsUpdate) {
          updateInput.push({
            id: descriptor.id,
            gradeScaleValueId: currentDescriptor?.gradeScaleValue?.id,
            elementId: _.get(criterion, "standard.id"),
            description: currentDescriptor?.description || null,
            elementType: getElementType(currentCriterion),
          });
        }
      }

      // Handle descriptor count differences
      if (currentCriterionDescriptorsCount > otherCriterionDescriptorsCount) {
        // Create new descriptors if current criterion has more descriptors
        for (
          let i = otherCriterionDescriptorsCount;
          i < currentCriterionDescriptorsCount;
          i++
        ) {
          const currentDescriptor = currentCriterionDescriptors[i];

          createInput.push({
            label: currentDescriptor?.label || "",
            description: currentDescriptor?.description || null,
            criteriaId: criterion.id,
            displaySequence: i + 1,
            gradeScaleValueId: currentDescriptor?.gradeScaleValue?.id,
            elementId: _.get(criterion, "standard.id"),
            elementType: getElementType(currentCriterion),
          });
        }
      } else if (
        currentCriterionDescriptorsCount < otherCriterionDescriptorsCount
      ) {
        // Delete extra descriptors if current criterion has fewer descriptors
        for (
          let i = currentCriterionDescriptorsCount;
          i < otherCriterionDescriptorsCount;
          i++
        ) {
          const descriptorToDelete = descriptors[i];
          if (descriptorToDelete?.id) {
            deleteIds.push(descriptorToDelete.id);
          }
        }
      }
    });

    setShowFullScreenLoader(true);

    try {
      let shouldRefetchTool = false;
      // Update existing descriptors
      if (_.size(updateInput) > 0) {
        await updateStandardRubricDescriptors({
          toolId,
          input: updateInput,
          type: assessmentToolType,
        });
      }

      // Create new descriptors
      if (_.size(createInput) > 0) {
        await bulkCreateStandardRubricDescriptors({
          input: createInput,
          toolId,
          type: assessmentToolType,
        });

        shouldRefetchTool = true;
      }

      // Delete extra descriptors
      if (_.size(deleteIds) > 0) {
        await removeStandardRubricDescriptors({
          toolId,
          ids: deleteIds,
          type: assessmentToolType,
        });
      }

      if (shouldRefetchTool) {
        await getAssessmentToolNodeDetails({
          id: toolId,
          type: "STANDARD_RUBRIC",
        });
      }

      // Update display sequence for descriptors within each criterion (except current)
      const descriptorsDisplaySequenceInput = [];

      _.forEach(localRubricCriteria, criterion => {
        if (criterion.id !== currentCriterion.id) {
          // Update display sequence for descriptors within this criterion
          const descriptors = _.get(criterion, "descriptors", []);
          _.forEach(descriptors, (descriptor, descriptorIndex) => {
            descriptorsDisplaySequenceInput.push({
              id: descriptor.id,
              displaySequence: descriptorIndex + 1,
              elementType: getElementType(currentCriterion),
            });
          });
        }
      });

      if (_.size(descriptorsDisplaySequenceInput) > 0) {
        await updateStandardRubricDescriptors({
          toolId,
          input: descriptorsDisplaySequenceInput,
          type: assessmentToolType,
        });
      }
    } finally {
      setShowFullScreenLoader(false);
    }
  };

  const renderAddLine = () => {
    let showAddLine = false;

    if (!isStandardBasedRubric) {
      showAddLine = isCriteria || !isDragging;
    } else if (allowGradeScaleValueSelection) {
      const descriptorsCount = _.size(
        (isCriteria ? item : parentCriterion)?.descriptors
      );

      showAddLine = !isDragging && descriptorsCount < _.size(gradeScaleValues);
    }

    if (showAddLine) {
      return (
        <AddLine
          id={id}
          isHover={true}
          onAdd={() => onAddDescriptor?.(id)}
          testId={`${testId}-${id}-add-line-icon`}
          onKeyDown={onAddLineKeyDown}
        />
      );
    }

    // Hide placeholder line for the last descriptor when total points column is visible
    const shouldShowTotalPoints = isStandardBasedRubric && isNumericGradeScale;
    if (isLastDescriptor && shouldShowTotalPoints) {
      return null;
    }

    return <span className={classes.placeholderLine} />;
  };

  const renderLabel = () => {
    // Standard-based rubric label rendering:
    // 1. Dropdown for grade scale selection (ratings only) if allowGradeScaleValueSelection is true
    // 2. Static label with code display otherwise (no TextAreaInput due to styling constraints, code has to be shown will different styles)

    if (isStandardBasedRubric) {
      if (!isCriteria && allowGradeScaleValueSelection) {
        return (
          <div className={classes.dropdownContainer}>
            <Dropdown
              overlay={
                <DropdownMenu
                  options={gradeScaleValueOptions}
                  value={gradeScaleSelectedValue}
                  onClick={onSelectGradeScale}
                  showSelection={true}
                  selectionType={"tick"}
                  disabled={disabled}
                />
              }
              onVisibleChange={onChangeIsDropdownVisible}
              disabled={disabled}
            >
              <ButtonDisclosure
                type="plain"
                variant="neutral"
                size={"small"}
                isFullWidth={true}
                ref={updateLabelInputRef}
                disabled={disabled}
              >
                <span className={classes.truncate}>
                  {renderDropdownLabel(
                    _.find(gradeScaleValues, { key: gradeScaleSelectedValue }),
                    showGradeScaleValue
                  ) || t("common:select")}
                </span>
              </ButtonDisclosure>
            </Dropdown>

            {/* label should be unique */}
            {showUniqueGradeValuesError ? (
              <Tooltip
                tooltip={t(
                  "classRoom:grade_values_must_be_unique_within_a_criterion"
                )}
                placement={"top"}
              >
                <WarningTriangleFilled
                  size="xxx-small"
                  variant="critical"
                  className={classes.noShrink}
                />
              </Tooltip>
            ) : null}
          </div>
        );
      }

      return (
        <div className={classes.standardLabelWithDropdown}>
          <div className={classes.standardLabelContainer}>
            {/* common for both criteria and rating */}
            <span className={classNames("text-body", classes.standardLabel)}>
              {isCriteria ? localLabel : gradeScaleAbbreviation}
            </span>

            {/* just for criteria */}
            {standard?.code && showStandardCode ? (
              <span className={classNames("text-body", classes.subtleItalic)}>
                {` (${standard.code})`}
              </span>
            ) : null}

            {/* just for rating */}
            {localLabel && localLabel !== "-" && showGradeScaleValue ? (
              <span className={classNames("text-body", classes.subtleItalic)}>
                {` (${localLabel})`}
              </span>
            ) : null}
          </div>

          {isCriteria &&
          allowGradeScaleValueSelection &&
          showCopyToOtherCriteriaButton ? (
            <div className={classes.copyToOtherDropdown}>
              <Dropdown
                overlay={
                  <DropdownMenu
                    options={copyToOtherCriterionsOptions}
                    onClick={onCopyGradeScaleToOtherCriterions}
                  />
                }
              >
                <IconButton
                  icon={<DotsVerticalOutlined />}
                  size={"x-small"}
                  variant={"neutral"}
                  type={"plain"}
                  isCompact={true}
                />
              </Dropdown>
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <div className={classes.labelWithIcon}>
        <TextAreaInput
          placeholder={
            isCriteria ? t("common:criteria") : t("common:definition")
          }
          minRows={1}
          value={localLabel}
          updateInputField={updateLabelField}
          onBlurInputField={onBlurLabelField}
          updateInputRef={updateLabelInputRef}
          onKeyDown={onLabelTextAreaKeyDown}
          textAreaStyles={labelTextAreaStyles}
          customErrorClass={classes.error}
          disabled={disabled || isStandardBasedRubric}
          testId={`${testId}-${id}-description-input`}
          dsVersion={"2.0"}
        />
      </div>
    );
  };

  const labelAndDescriptionClass = classNames(classes.labelAndDescription, {
    [classes.labelWithoutDescription]:
      !showDescription && !isStandardBasedRubric,
    [classes.labelWithoutScore]: !showScore || isStandardBasedRubric,
    [classes.disabled]: disabled,
    [classes.standardRubricLabel]: isStandardBasedRubric,
  });

  const labelWithIconClass = classNames(classes.labelWithIcon);

  return (
    <div id={`ROW_CELL_CONTAINER_${id}`} className={classes.childrenContainer}>
      <div id={`ROW_CELL_${id}`} className={classes.rowCell}>
        {/* label and description section */}
        <div className={labelAndDescriptionClass}>
          <div className={labelWithIconClass}>
            {renderLabel()}

            {showResetToDefaultIcon ? (
              <div className={classes.resetIconContainer}>
                <Tooltip
                  showArrow={true}
                  placement="bottom"
                  tooltip={t("common:reset_to_default")}
                >
                  <IconButton
                    icon={<ReloadArrowOutlined />}
                    size={"x-small"}
                    variant={"neutral"}
                    type={"outlined"}
                    isCompact={true}
                    onClick={onClickResetToDefault}
                  />
                </Tooltip>
              </div>
            ) : null}
          </div>

          {showDescription ? (
            <TextAreaInput
              placeholder={t("common:add_a_with_label", {
                label: t("common:description"),
              })}
              minRows={1}
              value={localDescription}
              updateInputField={updateDescriptionField}
              onBlurInputField={onBlurDescriptionField}
              updateInputRef={updateDescriptionInputRef}
              textAreaStyles={descriptionTextAreaStyles}
              customErrorClass={classes.error}
              disabled={disabled || (isCriteria && isStandardBasedRubric)}
              testId={`${testId}-${id}-description-input`}
              dsVersion={"2.0"}
            />
          ) : null}
        </div>

        {/* points input section */}
        {!isStandardBasedRubric ? (
          <div className={classes.relative}>
            {showScore ? (
              <div className={classes.pointTextArea}>
                <TextAreaInput
                  placeholder={pointPlaceholder}
                  minRows={1}
                  value={!_.isNil(pointValue) ? `${pointValue}` : ""}
                  updateInputField={updatePointField}
                  onBlurInputField={onBlurPointField}
                  updateInputRef={updatePointInputRef}
                  textAreaStyles={pointTextAreaStyles}
                  customErrorClass={classes.error}
                  disabled={isCriteria || disabled || isStandardBasedRubric}
                  testId={`${testId}-${id}-score-input`}
                  dsVersion={"2.0"}
                />

                {isReordering && (
                  <div className={classes.reorderingIndicator} />
                )}
              </div>
            ) : null}

            {/* error descriptor for duplicate points */}
            {showUniquePointError ? (
              <Tooltip
                tooltip={t("classRoom:point_values_must_be_unique_msg")}
                placement="top"
              >
                <WarningTriangleFilled
                  size="xxx-small"
                  variant="critical"
                  className={classes.warningIcon}
                />
              </Tooltip>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* add line descriptor when not dragging for score based rubric */}
      {renderAddLine()}

      {showFullScreenLoader ? <FullScreenLoader /> : null}
    </div>
  );
});

StandardRubricEditCell.displayName = "StandardRubricEditCell";

const mapActionCreators = {
  updateStandardRubricCriterias,
  updateStandardRubricDescriptors,
  removeStandardRubricDescriptors,
  bulkCreateStandardRubricDescriptors,
  getAssessmentToolNodeDetails,
};

export default connect(null, mapActionCreators)(StandardRubricEditCell);
